export type AiAnalysisFilePayload = {
  filename: string;
  content_type?: string;
  data: string;
};

export const AI_ANALYSIS_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const MAX_TOTAL_BYTES = 20 * 1024 * 1024;

const EXTENSION_CONTENT_TYPE: { [key: string]: string } = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

const ALLOWED_EXTENSIONS = new Set(Object.keys(EXTENSION_CONTENT_TYPE));

const getExtension = (filename: string): string => {
  const parts = filename.toLowerCase().split('.');

  return parts.length > 1 ? parts.pop() ?? '' : '';
};

export const isAllowedAiAnalysisFile = (file: File): boolean => {
  return ALLOWED_EXTENSIONS.has(getExtension(file.name));
};

export const validateAiAnalysisFiles = (
  existing: Array<File>,
  incoming: Array<File>
): { files: Array<File>; error: string | null } => {
  const accepted: Array<File> = [...existing];

  for (const file of incoming) {
    if (!isAllowedAiAnalysisFile(file)) {
      return {
        files: existing,
        error: `Unsupported file type for '${file.name}'. Allowed: PDF, JPG, JPEG, PNG.`,
      };
    }

    const nextTotal = [...accepted, file].reduce((sum, item) => sum + item.size, 0);

    if (nextTotal > MAX_TOTAL_BYTES) {
      return {
        files: existing,
        error: `Total file size must not exceed 20 MB (got ${(nextTotal / (1024 * 1024)).toFixed(1)} MB).`,
      };
    }

    accepted.push(file);
  }

  return {
    files: accepted,
    error: null,
  };
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);

    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

export const filesToPayloads = async (
  files: Array<File>
): Promise<Array<AiAnalysisFilePayload>> => {
  return Promise.all(
    files.map(async file => {
      const extension = getExtension(file.name);
      const contentType =
        file.type || EXTENSION_CONTENT_TYPE[extension] || 'application/octet-stream';
      const buffer = await file.arrayBuffer();

      return {
        filename: file.name,
        content_type: contentType,
        data: arrayBufferToBase64(buffer),
      };
    })
  );
};
